import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import CrossIcon from '@atlaskit/icon/glyph/cross';
import Modal, {
  ModalHeader,
  ModalTitle,
  ModalTransition
} from '@atlaskit/modal-dialog';
import { N500 } from '@atlaskit/theme/colors';

import { SanitizedFormattedHtmlMessage } from '@atlassiansox/bxpkit-core';
import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { WacFreeBundlesModalPropsType } from '../model';
import { messages, modalMessagesByProductKey } from './messages';
import { ProductAddedView, SiteReadyView } from './modal-confirmation';
import { SecondaryProductSelectListModal } from './secondary-product-select-list';
import {
  ModalAnimate,
  ModalButton,
  ModalButtonContainer,
  ModalHeading,
  ModalProductSelectWrapper,
  ModalSection,
  ModalSubHeading,
  ModalTermsAndConditionsText,
  ModalWrapper
} from './styled';

export function WacFreeBundlesModal({
  isModalOpen,
  setIsModalOpen,
  primaryProductKey,
  secondaryProductKeys,
  selectedSecondaryProductKey,
  setSelectedSecondaryProductKey,
  bundleProvisioningStatus,
  setBundleProvisioningStatus,
  isSecondaryProductActivating,
  handleAgreeAndSignup
}: WacFreeBundlesModalPropsType) {
  const { createAnalyticsEvent } = useAnalyticsEvents();

  const [currentView, setCurrentView] = useState(0); // Views 0 - Bundles, 1 - Site Ready, 2 - Product Added

  const closeModal = useCallback((sendEvent = true, setStatus = true) => {
    // Prevent manual closing when timeout scheduled
    if (currentView === 2) {
      return;
    }

    if (sendEvent) {
      createAnalyticsEvent({
        eventName: 'bundleModalClose',
        eventComponent: 'button',
        event: 'clicked',
        modalView:
          currentView === 0
            ? 'modalBundles'
            : currentView === 1
            ? 'modalReady'
            : 'modalProductAdded'
      }).fire();
    }

    setIsModalOpen(false);

    if (setStatus) {
      // Update status to success, which means use is done interacting with modal
      setBundleProvisioningStatus('success');
    }
  }, []);

  // Handle the provisioning status changes when primary is ready or secondary is submitted
  useEffect(() => {
    // prevent view changes when showing secondary product added
    if (currentView === 2) {
      return;
    }

    switch (bundleProvisioningStatus) {
      case 'provisioning': // Primary product provisioned
        setCurrentView(1);
        return;
      case 'payload-ready': // Secondary product provisioning started
        setCurrentView(2);
        // Auto-close the modal after 2 seconds
        setTimeout(() => {
          closeModal(false, false);
        }, 2000);
    }
  }, [bundleProvisioningStatus]);

  // if isModalOpen changes to open reset to 0
  useEffect(() => {
    if (isModalOpen) {
      // Set bundle status to waiting to indicate user is still using the modal
      setBundleProvisioningStatus('waiting');
      setCurrentView(0);
    }
  }, [isModalOpen]);

  const takeMeBack = () => {
    createAnalyticsEvent({
      eventName: 'bundleModalTakeMeBack',
      eventComponent: 'button',
      event: 'clicked'
    }).fire();
    setCurrentView(0);
  };

  const noThanks = () => {
    createAnalyticsEvent({
      eventName: 'bundleModalNoThanks',
      eventComponent: 'button',
      event: 'clicked'
    }).fire();
    closeModal(false);
  };

  const goToProduct = () => {
    createAnalyticsEvent({
      eventName: 'bundleModalGoToProduct',
      eventComponent: 'button',
      event: 'clicked'
    }).fire();
    closeModal(false);
  };

  const AddButtonGroup = () => {
    return (
      <ModalButtonContainer>
        {selectedSecondaryProductKey && (
          <>
            <ModalTermsAndConditionsText>
              <SanitizedFormattedHtmlMessage
                {...messages.termsAndConditionsModalLabel}
              />
            </ModalTermsAndConditionsText>

            <ModalButton
              data-testid={'agree-btn'}
              appearance="primary"
              onClick={handleAgreeAndSignup}
              isLoading={isSecondaryProductActivating}
            >
              <FormattedMessage {...messages.agreeAndAdd} />
            </ModalButton>
          </>
        )}
        {!selectedSecondaryProductKey && (
          <ModalButton
            data-testid={'no-thanks-btn'}
            appearance="link"
            onClick={noThanks}
          >
            <FormattedMessage {...messages.noThanks} />
          </ModalButton>
        )}
      </ModalButtonContainer>
    );
  };

  const BundlesHeading = () => {
    return (
      <>
        <ModalHeading>
          <FormattedMessage
            {...messages.freeBundlesModalHeader}
            values={{
              PRODUCT_NAME: (
                <FormattedMessage
                  {...modalMessagesByProductKey[primaryProductKey].name}
                />
              )
            }}
          />
        </ModalHeading>
        <ModalSubHeading>
          <SanitizedFormattedHtmlMessage
            {...messages.freeBundlesModalSubHeader}
          />
        </ModalSubHeading>
        <ModalProductSelectWrapper>
          <SecondaryProductSelectListModal
            secondaryProductKeys={secondaryProductKeys}
            selectedSecondaryProductKey={selectedSecondaryProductKey}
            setSelectedSecondaryProductKey={setSelectedSecondaryProductKey}
          />
          <AddButtonGroup />
        </ModalProductSelectWrapper>
      </>
    );
  };

  return (
    <ModalTransition>
      {isModalOpen && (
        <Modal
          width={'970px'}
          height={'607px'}
          shouldCloseOnOverlayClick={true}
          shouldCloseOnEscapePress={true}
          autoFocus={false}
          onClose={closeModal}
        >
          <ModalHeader style={{ padding: '26px 0 24px' }}>
            <ModalTitle></ModalTitle>
            <Button appearance="link" onClick={closeModal}>
              <CrossIcon label="Close Modal" primaryColor={N500} />
            </Button>
          </ModalHeader>
          <ModalWrapper tabIndex={-1}>
            <ModalAnimate activeIndex={currentView}>
              <ModalSection current={currentView} index={0}>
                <BundlesHeading />
              </ModalSection>
              <ModalSection current={currentView} index={1}>
                <SiteReadyView
                  productKey={primaryProductKey}
                  goToProduct={goToProduct}
                  takeMeBack={takeMeBack}
                ></SiteReadyView>
              </ModalSection>
              <ModalSection current={currentView} index={2}>
                {selectedSecondaryProductKey && (
                  <ProductAddedView
                    secondaryProductKey={selectedSecondaryProductKey}
                  ></ProductAddedView>
                )}
              </ModalSection>
            </ModalAnimate>
          </ModalWrapper>
        </Modal>
      )}
    </ModalTransition>
  );
}
